import { Box, Grid, IconButton, MenuItem } from "@mui/material";
import { FormTextField, GridCardContent } from "components/atoms";
import { FormContainer, FormContent } from "components/molecules";
import { useFormik } from "formik";
import { AlertHelper, DialogHelper, RoleHelper, TableHelper } from "helpers";
import { EAlertType } from "helpers/alertHelper";
import {
  EInprocessCheckType,
  EQualityCheckingConditionType,
  IApiResponse,
  IProductGroup,
  IQualityCondition,
} from "model";
import BasePage from "pages/base";
import { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { MasterDataApiService, QualityConditionApiService } from "services";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";
import { QualityConditionDetailModal } from "./QualityConditionDetailModal";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { ECommonDialogType } from "components/molecules/CommonDialog";
import { theme } from "themes";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";

export const ProductGroupDetail = () => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const history = useHistory();
  const isAdd = location.pathname.includes("new");
  const [isGetProductGroupIdLoading, setIsGetProductGroupByIdLoading] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedQualityCondition, setSelectedQualityCondition] =
    useState<IQualityCondition>();
  const [isOpenQualityConditionDetail, setIsOpenQualityConditionDetail] =
    useState<boolean>(false);
  const formik = useFormik<
    Partial<IProductGroup> & { qualityConditions?: IQualityCondition[] }
  >({
    initialValues: {},
    onSubmit: (values: Partial<IProductGroup>) => {
      if (isAdd) {
        addProductGroup(values);
      } else {
        editProductGroup(values as IProductGroup);
      }
    },
    validationSchema: validationSchema,
  });

  const COLUMNS = [
    {
      ...TableHelper.renderSTT(),
      sortable: false,
      disableColumnMenu: true,
    } as GridColDef<any, IQualityCondition>,
    TableHelper.createCell<IQualityCondition>({
      field: "action2",
      headerName: I18nTranslator(translations.report.action),
      size: 100,
      renderCell: ({ row }) => (
        <>
          <IconButton
            sx={{
              display: !(RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor())
                ? "none"
                : "inline",
            }}
            disabled={!(RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor())}
            onClick={() => {
              setSelectedQualityCondition(row);
              setIsOpenQualityConditionDetail(true);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            sx={{
              display: !(RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor())
                ? "none"
                : "inline",
            }}
            disabled={!(RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor())}
            onClick={() => onDeleteQualityConditionClick(row.id!)}
          >
            <DeleteIcon
              sx={{ color: theme.colors.error.light }}
              fontSize="small"
            />
          </IconButton>
        </>
      ),
      sortable: false,
      disableColumnMenu: true,
    }),
    TableHelper.createCell({
      field: "name",
      headerName: I18nTranslator(translations.name),
      sortable: false,
      disableColumnMenu: true,
      size: "full",
    }),
    TableHelper.createCell({
      field: "nameEN",
      headerName: I18nTranslator(translations.productGroup.enName),
      sortable: false,
      disableColumnMenu: true,
      size: "full",
    }),
    TableHelper.createCell<IQualityCondition>({
      field: "type",
      headerName: I18nTranslator(translations.productGroup.conditionValueType),
      renderCell: ({ row }) =>
        row.type === EQualityCheckingConditionType.InputText
          ? I18nTranslator(translations.productGroup.inputText)
          : I18nTranslator(translations.productGroup.predefined),
      sortable: false,
      disableColumnMenu: true,
    }),
    TableHelper.createCell<IQualityCondition>({
      field: "imageRequire",
      headerName: I18nTranslator(translations.productGroup.imageRequired),
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        row.imageRequired
          ? I18nTranslator(translations.productGroup.required)
          : I18nTranslator(translations.productGroup.notRequired),
    }),
    TableHelper.createCell({
      field: "displayOrder",
      headerName: I18nTranslator(translations.productGroup.displayOrder),
      sortable: false,
      disableColumnMenu: true,
    }),
    TableHelper.createCell<IQualityCondition>({
      field: "action",
      headerName: I18nTranslator(translations.report.action),
      renderCell: ({ row }) => (
        <div>
          <IconButton
            sx={{
              display: !(RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor())
                ? "none"
                : "inline",
            }}
            disabled={!(RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor())}
            onClick={() => {
              setSelectedQualityCondition(row);
              setIsOpenQualityConditionDetail(true);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            sx={{
              display: !(RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor())
                ? "none"
                : "inline",
            }}
            disabled={!(RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor())}
            onClick={() => onDeleteQualityConditionClick(row.id!)}
          >
            <DeleteIcon
              sx={{ color: theme.colors.error.light }}
              fontSize="small"
            />
          </IconButton>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    }),
  ];

  useEffect(() => {
    if (params.id) {
      getProductGroupById(params.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const getFormTitle = () => {
    return isAdd
      ? I18nTranslator(translations.productGroup.addProductGroup)
      : I18nTranslator(translations.productGroup.editProductGroup);
  };

  const getProductGroupById = async (id: string) => {
    try {
      setIsGetProductGroupByIdLoading(true);
      const result = (await MasterDataApiService.getProductGroupById(
        id
      )) as IApiResponse<IProductGroup>;
      if (result.succeeded && result.data) {
        formik.setValues(result.data!);
        getAllQualityCondition(result.data.id);
      } else {
        AlertHelper.showError(result);
      }
    } catch (error) {
    } finally {
      setIsGetProductGroupByIdLoading(false);
    }
  };

  const getAllQualityCondition = async (groupId: string) => {
    try {
      setIsLoading(true);
      const result = (await QualityConditionApiService.getAllQualityCondition({
        groupId: groupId,
        pageNumber: 1,
        pageSize: 9999,
      })) as IApiResponse<IQualityCondition[]>;
      if (result.succeeded && result.data) {
        formik.setFieldValue("qualityConditions", result.data);
      } else {
        AlertHelper.showError(result);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const deleteQualityCondition = async (id: string) => {
    try {
      setIsLoading(true);
      const result = (await QualityConditionApiService.deleteQualityCondition(
        id
      )) as IApiResponse<string>;
      if (result.succeeded && result.data) {
        AlertHelper.showAlert(
          I18nTranslator(
            translations.productGroup.deleteQualityConditionSuccess
          ),
          EAlertType.SUCCESS
        );
        getAllQualityCondition(formik.values.id!);
      } else {
        AlertHelper.showError(result);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const addProductGroup = async (request: Partial<IProductGroup>) => {
    try {
      setIsLoading(true);
      const result = (await MasterDataApiService.addProductGroup(
        request
      )) as IApiResponse<IProductGroup>;
      if (result.succeeded && result.data) {
        formik.setValues(result.data!);
        formik.setFieldValue("qualityConditions", []);
        AlertHelper.showAlert(
          I18nTranslator(translations.productGroup.addSuccess),
          EAlertType.SUCCESS
        );
        history.replace(result.data!.id!);
      } else {
        AlertHelper.showError(result);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const editProductGroup = async (request: IProductGroup) => {
    try {
      setIsLoading(true);
      const result = (await MasterDataApiService.editProductGroup(
        request
      )) as IApiResponse<IProductGroup>;
      if (result.succeeded && result.data) {
        formik.setValues({ ...formik.values, ...result.data! });
        AlertHelper.showAlert(
          I18nTranslator(translations.productGroup.updateSuccess),
          EAlertType.SUCCESS
        );
      } else {
        AlertHelper.showError(result);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteQualityConditionClick = (id: string) => {
    DialogHelper.open({
      title: I18nTranslator(translations.dialog.warning),
      message: I18nTranslator(
        translations.productGroup.deleteQualityConditionConfirm
      ),
      type: ECommonDialogType.WARNING,
      onConfirm: () => deleteQualityCondition(id),
      isCancel: true,
    });
  };

  const onClickDeleteProductGroup = () => {
    DialogHelper.open({
      title: I18nTranslator(translations.dialog.warning),
      message: I18nTranslator(translations.productGroup.deleteConfirm),
      type: ECommonDialogType.WARNING,
      onConfirm: () => deleteProductGroup(formik.values.id!),
      isCancel: true,
    });
  };

  const deleteProductGroup = async (id: string) => {
    try {
      setIsLoading(true);
      const result = (await MasterDataApiService.deleteProductGroup(
        id
      )) as IApiResponse<string>;
      if (result.succeeded) {
        AlertHelper.showAlert(
          I18nTranslator(translations.productGroup.deleteSuccess),
          EAlertType.SUCCESS
        );
        history.go(-1);
      } else {
        AlertHelper.showError(result);
      }
    } catch (error) {
      console.error("Network error", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BasePage loading={isGetProductGroupIdLoading}>
      <GridCardContent>
        <Box sx={{ bgcolor: "white", p: 2, height: "100%", width: "100%" }}>
          <FormContainer
            formik={formik}
            loading={isLoading}
            isAdd={isAdd}
            title={getFormTitle()}
            onDelete={onClickDeleteProductGroup}
            disableDelete={
              (!RoleHelper.isSuperAdmin() && !RoleHelper.isAdmin()) ||
              !formik.values.id
            }
            disableSubmit={!RoleHelper.isSuperAdmin() && !RoleHelper.isAdmin()}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormTextField
                  id="name"
                  label={I18nTranslator(translations.name)}
                  placeholder={I18nTranslator(translations.name)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  label={I18nTranslator(
                    translations.productGroup.inprocessCheckType
                  )}
                  select
                  id="checkType"
                >
                  <MenuItem value={EInprocessCheckType.TIME}>
                    {I18nTranslator(translations.productGroup.checkByTime)}
                  </MenuItem>
                  <MenuItem value={EInprocessCheckType.UNIT}>
                    {I18nTranslator(translations.productGroup.checkByUnit)}
                  </MenuItem>
                </FormTextField>
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  label={I18nTranslator(
                    translations.productGroup.productionType
                  )}
                  select
                  id="isLocal"
                >
                  <MenuItem value={"true"}>
                    {I18nTranslator(translations.productGroup.local)}
                  </MenuItem>
                  <MenuItem value={"false"}>
                    {I18nTranslator(translations.productGroup.export)}
                  </MenuItem>
                </FormTextField>
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  id="description"
                  label={I18nTranslator(translations.description)}
                  placeholder={I18nTranslator(translations.description)}
                  multiline
                  minRows={3}
                />
              </Grid>
              <Grid item xs={12}>
                {formik.values.qualityConditions !== undefined && (
                  <FormContent
                    title={I18nTranslator(
                      translations.productGroup.qualityConditions
                    )}
                  >
                    <Grid
                      item
                      xs={12}
                      style={{
                        height: 300,
                        marginTop: "10px",
                        width: "100%",
                      }}
                    >
                      <DataGrid
                        loading={isLoading}
                        rows={
                          formik.values.qualityConditions?.map((e, index) => ({
                            no: index + 1,
                            ...e,
                          })) || []
                        }
                        columns={COLUMNS}
                        hideFooterPagination
                        hideFooterSelectedRowCount
                        // onCellEditCommit={handleEditCell}
                        className="table-custom"
                        components={{
                          Footer: () => (
                            <LoadingButton
                              color="primary"
                              variant="contained"
                              sx={{
                                marginTop: 2,
                              }}
                              onClick={() =>
                                setIsOpenQualityConditionDetail(true)
                              }
                              disabled={isLoading}
                              startIcon={<AddIcon fontSize={"small"} />}
                            >
                              {I18nTranslator(
                                translations.productGroup.addQualityCondition
                              )}
                            </LoadingButton>
                          ),
                        }}
                      />
                    </Grid>
                  </FormContent>
                )}
              </Grid>
            </Grid>
            {formik.values.id && !isGetProductGroupIdLoading && (
              <QualityConditionDetailModal
                groupId={formik.values.id}
                onSuccess={() => {
                  setIsOpenQualityConditionDetail(false);
                  setSelectedQualityCondition(undefined);
                  //recall api
                  getAllQualityCondition(formik.values.id!);
                }}
                open={isOpenQualityConditionDetail}
                qualityCondition={selectedQualityCondition}
                handleClose={() => {
                  setIsOpenQualityConditionDetail(false);
                  setSelectedQualityCondition(undefined);
                }}
              />
            )}
          </FormContainer>
        </Box>
      </GridCardContent>
    </BasePage>
  );
};

const validationSchema = yup.object().shape({
  name: yup.string().required(I18nTranslator(translations.yupError.required)),
});
